/**
 * Cashfree JS SDK v3 loader and checkout helper.
 * SDK docs: https://docs.cashfree.com/docs/web-checkout-seamless
 */

const CASHFREE_SDK_URL = 'https://sdk.cashfree.com/js/v3/cashfree.js';

let sdkLoaded = false;

/**
 * Dynamically load the Cashfree JS SDK v3.
 * Single URL for both sandbox and production — mode is passed at runtime.
 */
function loadCashfreeSDK() {
  return new Promise((resolve, reject) => {
    if (sdkLoaded && window.Cashfree) {
      resolve(window.Cashfree);
      return;
    }

    const existingScript = document.querySelector('script[data-cashfree-v3]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.Cashfree));
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = CASHFREE_SDK_URL;
    script.setAttribute('data-cashfree-v3', '1');
    script.onload = () => {
      sdkLoaded = true;
      resolve(window.Cashfree);
    };
    script.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
    document.head.appendChild(script);
  });
}

/**
 * Open the Cashfree drop-in checkout (SDK v3).
 *
 * @param {object} options
 * @param {string} options.paymentSessionID - from backend InitiateRecharge response
 * @param {string} options.orderID          - gateway order ID (used in verify call)
 * @param {boolean} options.sandbox         - true for sandbox mode
 * @param {function} options.onSuccess      - called when payment succeeds
 * @param {function} options.onFailure      - called with error object
 */
export async function openCashfreeCheckout({ paymentSessionID, orderID, sandbox = true, onSuccess, onFailure }) {
  try {
    await loadCashfreeSDK();

    // SDK v3: Cashfree() is called as a function (no `new`)
    const cashfree = window.Cashfree({
      mode: sandbox ? 'sandbox' : 'production',
    });

    const result = await cashfree.checkout({
      paymentSessionId: paymentSessionID,
      redirectTarget: '_modal',
    });

    // ALWAYS attempt verification after the modal closes — regardless of SDK result.
    // When user manually closes the modal, the SDK returns result.error even if
    // Cashfree already captured the payment. Backend will check actual order status.
    try {
      if (onSuccess) await onSuccess({ orderId: orderID, paymentDetails: result.paymentDetails });
    } catch (verifyErr) {
      // Verify failed — payment was not captured (user closed before paying)
      if (onFailure) onFailure(result.error || verifyErr);
    }
  } catch (err) {
    if (onFailure) onFailure(err);
  }
}
